import React from 'react';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/solid';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: { value: number; label: string; isPositive?: boolean };
  icon?: React.ReactNode;
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'purple' | 'indigo';
}

const ACCENT: Record<string, { top: string; icon: string; text: string; bg: string }> = {
  blue:   { top: '#0ea5e9', icon: '#0ea5e9', text: '#0369a1', bg: '#f0f9ff' },
  green:  { top: '#10b981', icon: '#10b981', text: '#065f46', bg: '#ecfdf5' },
  yellow: { top: '#f59e0b', icon: '#f59e0b', text: '#92400e', bg: '#fffbeb' },
  red:    { top: '#ef4444', icon: '#ef4444', text: '#991b1b', bg: '#fef2f2' },
  purple: { top: '#8b5cf6', icon: '#8b5cf6', text: '#4c1d95', bg: '#f5f3ff' },
  indigo: { top: '#6366f1', icon: '#6366f1', text: '#312e81', bg: '#eef2ff' },
};

export default function StatCard({ title, value, subtitle, trend, icon, color = 'blue' }: StatCardProps) {
  const a = ACCENT[color];
  return (
    <div
      className="relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5"
      style={{ border: '1px solid rgba(0,0,0,0.06)' }}
    >
      {/* top accent strip */}
      <div className="h-1 w-full" style={{ background: a.top }} />
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400">{title}</p>
          {icon && (
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: a.bg }}>
              <div style={{ color: a.icon }} className="[&_svg]:w-4 [&_svg]:h-4">{icon}</div>
            </div>
          )}
        </div>

        <p
          className="text-3xl font-black tabular-nums leading-none"
          style={{ color: a.text, fontVariantNumeric: 'tabular-nums' }}
        >
          {typeof value === 'number' ? value.toLocaleString('fr-FR') : value}
        </p>

        <div className="mt-3 flex items-center justify-between">
          {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
          {trend && (
            <span
              className={`inline-flex items-center gap-0.5 text-xs font-semibold px-2 py-0.5 rounded-full ${
                trend.isPositive !== false ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'
              }`}
            >
              {trend.isPositive !== false
                ? <ArrowUpIcon className="w-3 h-3" />
                : <ArrowDownIcon className="w-3 h-3" />}
              {Math.abs(trend.value)}%
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
